// Animate, the third stage of a picture: the Maker for movement, as the body of the stage page (see PicturePage).
// "Start from" holds the still picture and every animation made for it (V1,
// V2… newest first); tapping one puts it in use and fills the form from it. Below, how it is drawn, then what happens
// with the Animate button straight under it; then the picked animation big, looping at its own length as the timeline will draw it, with its play
// controls right under it. The preview is the timeline itself, so lip sync, feelings, camera and the wiggle all show.
// The old shot-based version is MotionWizard.
// Everything saves as it is changed. Making frames keeps going if the page is left (ui/jobs.ts): the new animation
// lands on the picture wherever the episode is, and the stage shows it is still drawing when opened again. The bin
// deletes the picked animation at once (undo brings it back) and the picture goes back to the still.
import { faceSpritesInUse } from "../../../library/faceSets";
import { useEffect, useMemo, useRef, useState } from "react";
import { motionJob, notify, runJob, useJob } from "../../jobs";
import { applyToEpisode } from "../episodeStore";
import { timelineFrameAt } from "../../../engine/timelineFrame";
import { decodeImages } from "../../../library/images";
import { useLibrary } from "../../../library/store";
import { findAsset } from "../../../model/library";
import { uid } from "../../../model/ids";
import type { ShotFace } from "../../../model/episode";
import {
  addMotion, markById, motionOf, resolve, placeholderOf, removeMotion, stretchPlaceholder, setMarker, setMotion, chooseMotion,
  type AnimFrame, type PictureMotion, type PlacedFace,
} from "../../../model/timeline";
import { FrameSvg } from "../../../render/FrameSvg";
import { api } from "../../api";
import { FitFrame, Icon, Section, Segmented, Slider, getImageModel, useIdleClock } from "../../common";
import { MakeRow, ResultFrame, VersionStrip } from "../../maker/Maker";
import { useDraft } from "../../drafts";
import { useTimeline } from "../TimelineLoader";
import { FrameFaces } from "./FrameFaces";

const HINTS = {
  loop: "Plays round and round for as long as the image shows, at the speed below.",
  once: "Plays through once at the speed below, then holds on its last frame.",
} as const;

export const playName = (p: PictureMotion["play"]) => (p === "loop" ? "Loops" : p === "once" ? "Once" : "Fills");
const ANIMATE_MS = 60_000; // how long the progress bar takes to fill

// Which way a character's placed face looks, carried onto the faces found on each frame.
const turnOf = (faces: PlacedFace[], char: string) => { const t = faces.find(p => p.char === char)?.face.turn; return t ? { turn: t } : {}; };

// What an animation was asked for: its words and whether it loops. Faces always follow the characters (marks drawn on
// each face show where it went); any that come out wrong are fixed by hand on "Faces on each frame".
type Ask = { prompt: string; loop: boolean };

export function MotionStage({ id }: { id: string }) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const mark = markById(ep, "picture", id);
  const motion = mark ? motionOf(mark) : undefined;
  const versions = mark?.motions ?? [];

  // The form starts from the animation in use, if any.
  const [prompt, setPrompt] = useDraft(`motion:${id}:prompt`, motion?.prompt ?? ""); // kept when the stage is left (ui/drafts.ts)
  const [loop, setLoop] = useState(motion ? motion.painted === "loop" : false);
  const busy = useJob(motionJob(id));
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const [failed, setFailed] = useState<Ask | null>(null); // the animation that went wrong, to try again
  const [faces, setFaces] = useState(false);
  const stage = useRef<HTMLDivElement>(null);

  if (!mark) return <p className="hint">Add a picture first.</p>;

  const dur = Math.max(0, mark.end - mark.t);
  const next = `V${versions.length + 1}`; // what the next animation will be called
  // The people in the picture, as quick buttons that put their name into the words.
  const cast = [...new Set(mark.faces.map(f => f.char))].map(c => ({ id: c, name: findAsset(lib, c)?.name })).filter((c): c is { id: string; name: string } => !!c.name);
  const addName = (name: string) => setPrompt(`${prompt}${prompt && !/\s$/.test(prompt) ? " " : ""}${name} `);
  const fill = (a: Ask) => { setPrompt(a.prompt); setLoop(a.loop); };

  async function make(ask: Ask) {
    if (busy !== null || !ask.prompt.trim()) return;
    setFailed(null);
    // The preview under the form shows the animation being made, then plays it.
    requestAnimationFrame(() => stage.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    try {
      const m = mark!, pic = m.picture;
      // Only characters with a face on the picture are sent (a face can be parked off it).
      const onIt: ShotFace[] = m.faces.filter(f => f.face.eyes.x > 0 && f.face.eyes.y > 0 && f.face.eyes.x < pic.w && f.face.eyes.y < pic.h).map(f => ({ ...f.face, character: f.char }));
      const chars = m.refs.filter(r => r.kind === "character");
      const onPicture = chars.filter(r => lib.assets.some(a => a.kind === "character" && a.picture.src === r.src && onIt.some(f => f.character === a.id)));
      const res = await runJob(motionJob(id), api.motionGrid({ picture: pic, faces: onIt, chars: onPicture.length ? onPicture : chars, prompt: ask.prompt.trim(), dots: true, loop: ask.loop, ...(m.style ? { style: m.style } : {}) }));
      const frames: AnimFrame[] = res.frames.map(f => ({
        picture: { src: f.src, w: f.w, h: f.h },
        // Only characters found on the frame; animFaces fills in the others from the nearest frame.
        found: (f.found ?? []).filter(x => m.faces.some(p => p.char === x.character)).map((x): PlacedFace => ({ char: x.character, face: { eyes: x.eyes, brows: x.brows, mouth: x.mouth, ...(x.tilt !== undefined ? { tilt: x.tilt } : {}), ...turnOf(m.faces, x.character) } })),
      }));
      const made: PictureMotion = { id: uid("mo"), made: Date.now(), model: getImageModel(), prompt: ask.prompt.trim(), dots: true, painted: ask.loop ? "loop" : "once", frames, play: "stretch", fps: 8 }; // fills the image's time to begin with
      // A picture whose only line is its untouched 1 s silence stretches it to the animation's own length (its frames,
      // and the picture itself, at its speed), so the animation plays in full straight away.
      await applyToEpisode(ep.id, e => {
        const added = addMotion(e, id, made);
        const ph = placeholderOf(added, id);
        return ph ? stretchPlaceholder(added, ph.id, (made.frames.length + 1) / made.fps) : added;
      }, { undoable: true });
      if (alive.current) setPrompt("");
    } catch (e) {
      if (alive.current) setFailed(ask); else notify(`Couldn't animate the picture: ${(e as Error).message}`);
    }
  }

  const patch = (p: Partial<Omit<PictureMotion, "id">>, merge?: string) => update(e => setMotion(e, id, motion!.id, p), merge ? { merge: `${merge}-${motion!.id}` } : undefined);
  // Still: no movement, and the form is empty. An animation: in use, with its words, loop and faces in the form.
  const pickStill = () => { update(e => chooseMotion(e, id, undefined)); fill({ prompt: "", loop: false }); };
  const pickMotion = (v: PictureMotion) => { update(e => chooseMotion(e, id, v.id)); fill({ prompt: v.prompt, loop: v.painted === "loop" }); };
  const n = motion ? versions.findIndex(v => v.id === motion.id) + 1 : 0;

  return (
    <>
      <VersionStrip hint="Every animation you make is kept. Tap one to use it and make another like it." disabled={busy !== null}
        fresh={{ label: "Still", sub: "No movement", src: mark.picture.src, on: !motion, onPick: pickStill }}
        making={busy !== null ? { label: next, word: "Animating" } : undefined}
        failed={failed && busy === null ? { label: next, onRetry: () => void make(failed) } : undefined}
        versions={versions.map((v, k) => ({ id: v.id, label: `V${k + 1}`, sub: playName(v.play), src: v.frames[0]?.picture.src ?? mark.picture.src, on: motion?.id === v.id, title: v.prompt })).reverse()}
        onPick={vid => { const v = versions.find(x => x.id === vid); if (v) pickMotion(v); }} />

      <Section title="How it's drawn" hint={loop ? "The movement comes back to where it started, so it can play round and round." : "The movement goes from start to finish once."}>
        <Segmented label="How it's drawn" value={loop ? "loop" : "once"} options={[["once", "Play once"], ["loop", "Loop"]]} onChange={v => setLoop(v === "loop")} />
      </Section>

      <Section title="What happens" hint="Say who moves and how, from how the image is now. Call people by their names.">
        {cast.length > 0 && (
          <div className="tl-chips left" role="group" aria-label="Add a name">
            {cast.map(c => <button key={c.id} className="tl-chip" disabled={busy !== null} onClick={() => addName(c.name)}>{c.name}</button>)}
          </div>
        )}
        <textarea className="input" rows={3} maxLength={1200} value={prompt} aria-label="What happens" disabled={busy !== null}
          placeholder="e.g. Dez stands up and walks towards the camera." onChange={e => setPrompt(e.target.value)} />
        <MakeRow model disabled={busy !== null || !prompt.trim()} onClick={() => void make({ prompt, loop })}>
          <Icon name="sparkle" /> Animate
        </MakeRow>
      </Section>

      <div ref={stage} className="an-watch">
        <ResultFrame busy={busy !== null ? { message: `Animating ${next}…`, since: busy, ms: ANIMATE_MS } : undefined}
          onDelete={motion ? () => update(e => removeMotion(e, id, motion.id)) : undefined} deleteLabel={`Delete V${n}`}>
          <Preview id={id} />
        </ResultFrame>
      </div>

      <Section title={motion ? `How V${n} plays` : "Wiggle"} hint={motion ? (motion.play === "stretch" ? `Fill: the movement is spread evenly across the whole time this image is on screen (now ${dur.toFixed(1)} s), so it starts as the image appears and ends as it goes.` : HINTS[motion.play]) : undefined}>
        {motion && (
          <>
            <Segmented label="How it plays" value={motion.play} options={[["stretch", "Fill"], ["once", "Once"], ["loop", "Loop"]]} onChange={v => patch({ play: v })} />
            {motion.play !== "stretch" && (
              <Slider label="Speed" min={4} max={16} value={motion.fps} onChange={fps => patch({ fps }, "fps")} show={v => `${v} frames a second`} left="Choppy" right="Smooth" />
            )}
          </>
        )}
        <Segmented label="Wiggle" value={mark.boil === false ? "off" : "on"} options={[["on", "Wiggle on"], ["off", "Wiggle off"]]}
          onChange={v => update(e => setMarker(e, "picture", id, { boil: v === "off" ? false : undefined }))} />
        {motion && <button className="btn small" onClick={() => setFaces(true)}>Faces on each frame <Icon name="chevron" size={16} /></button>}
      </Section>
      {faces && motion && <FrameFaces pictureId={id} motionId={motion.id} onBack={() => setFaces(false)} />}
    </>
  );
}

// The picked animation playing round and round at its own length, drawn as the timeline would draw it: its frames (the
// picture itself first) at its speed, whatever time the image has on the timeline. A "fill" animation uses the image's
// time, or its own length when the image has almost none. Lines, feelings and camera around the image still show.
function Preview({ id }: { id: string }) {
  const { ep } = useTimeline();
  const mark = markById(ep, "picture", id)!;
  const motion = motionOf(mark);
  const [ready, setReady] = useState(!motion);
  const urls = motion ? motion.frames.map(f => f.picture.src).join("|") : "";
  // The first frame is held until every frame and face sprite is loaded and decoded, then the clock starts.
  useEffect(() => {
    if (!motion) { setReady(true); return; }
    let live = true;
    setReady(false);
    void decodeImages([mark.picture.src, ...motion.frames.map(f => f.picture.src), ...faceSpritesInUse()]).then(() => live && setReady(true));
    return () => { live = false; };
  }, [motion?.id, urls]); // eslint-disable-line react-hooks/exhaustive-deps
  const clock = useIdleClock(ready);
  const imageTime = mark.end - mark.t;
  const frames = motion ? motion.frames.length + 1 : 1;
  const own = motion && motion.fps > 0 ? frames / motion.fps : 1; // the animation's own length
  const len = !motion ? Math.max(0.1, imageTime) : motion.play === "stretch" && imageTime >= 0.1 ? imageTime : own;
  // The same episode with the image carrying on past its own time (the pictures after it are left out) and the
  // animation set to loop at the speed that fits `len`, so every case plays as a plain loop.
  const shown = useMemo(() => {
    if (!motion) return ep;
    const later = new Set(resolve(ep).pictures.filter(p => p.t > mark.t + 1e-6).map(p => p.id));
    const looped: PictureMotion = { ...motion, play: "loop", fps: frames / len };
    return { ...ep, pictures: ep.pictures.filter(p => !later.has(p.id)).map(p => (p.id === id ? { ...p, motions: [looped], motionId: looped.id } : p)) };
  }, [ep, id, motion, mark.t, frames, len]);
  const at = ready ? mark.t + (clock % len) : mark.t;
  return (
    <div className="an-stage">
      <FitFrame>
        <FrameSvg id="motion-stage" frame={timelineFrameAt(shown, at, clock)} />
      </FitFrame>
    </div>
  );
}
